import { useMemo } from 'react';
import {
  SelectedData,
  WorkDetailSimpleData,
  WorkSimpleWithDetails,
} from '../../shared/dto/EntityRepository';
import { AlertPosition, AlertType, ModalType, ProjectElementType } from '../../shared/enum/EnumRepository';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import {
  FormationSlotReferenceAssignment,
  useProjectElementListStore,
} from '../../shared/store/projectElementStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useModalWorkTagStore } from '../../shared/store/workTagStore';
import { useModalPageStore } from '../../shared/store/pageStore';
import {
  getEmptyFormationSlotIndexes,
  getFormationItemSource,
  getVisibleFormationItems,
} from '../../shared/util/formationUtils';

export function clearFormationSlotImportModalState() {
  useImportStationWorkStore.getState().clearSimpleList();
  useModalWorkTagStore.getState().clearSelectedModalTags();
  useModalPageStore.getState().resetModalPage();
}

export function useFormationSlotImport() {
  const { standardModal, clearModal } = useModalStore();
  const updateFormationSlotReference = useProjectElementListStore((s) => s.updateFormationSlotReference);
  const updateFormationSlotReferences = useProjectElementListStore((s) => s.updateFormationSlotReferences);
  const targetFormation = useProjectElementListStore((s) => {
    const pe = s.projectElementList.find((item) => item.id === standardModal?.title);
    return pe?.projectElementType === ProjectElementType.FORMATION ? pe.formation : null;
  });
  const selectedDataList = useImportStationWorkStore((s) => s.selectedDataList);
  const replacementSlotIndexes = useImportStationWorkStore(
    (s) => s.formationReplacementSlotIndexes,
  );
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);
  const isBulkImport = standardModal?.modalType === ModalType.IMPORT_STATION_FOR_FORMATION;
  const emptySlotIndexes = useMemo(
    () => targetFormation
      ? getEmptyFormationSlotIndexes(targetFormation.items, targetFormation.formationType)
      : [],
    [targetFormation],
  );
  const formationItems = useMemo(
    () => targetFormation
      ? getVisibleFormationItems(targetFormation.items, targetFormation.formationType)
      : [],
    [targetFormation],
  );
  const visibleSlotIndexes = useMemo(
    () => new Set(formationItems.map((item) => item.slotIndex)),
    [formationItems],
  );
  const availableSlotIndexes = useMemo(
    () => [...new Set([...emptySlotIndexes, ...replacementSlotIndexes])]
      .filter((slotIndex) => visibleSlotIndexes.has(slotIndex))
      .sort((a, b) => a - b),
    [emptySlotIndexes, replacementSlotIndexes, visibleSlotIndexes],
  );

  const enqueueNoAvailableSlotAlert = () => {
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('FORMATION_IMPORT_NO_EMPTY_SLOT'),
    });
  };

  const importToSlot = (
    selected: SelectedData,
    parentWork?: WorkSimpleWithDetails,
    detail?: WorkDetailSimpleData,
  ) => {
    if (!standardModal?.title) return;

    if (isBulkImport) {
      const importStore = useImportStationWorkStore.getState();
      const alreadySelected = importStore.selectedDataList.some((item) => (
        item.workId === selected.workId && item.detailId === selected.detailId
      ));
      if (alreadySelected) return;

      const currentPe = useProjectElementListStore.getState().projectElementList.find(
        (item) => item.id === standardModal.title,
      );
      const currentEmptySlotIndexes = currentPe?.projectElementType === ProjectElementType.FORMATION
        ? getEmptyFormationSlotIndexes(
          currentPe.formation.items,
          currentPe.formation.formationType,
        )
        : [];
      const currentVisibleSlotIndexes = currentPe?.projectElementType === ProjectElementType.FORMATION
        ? new Set(getVisibleFormationItems(
          currentPe.formation.items,
          currentPe.formation.formationType,
        ).map((item) => item.slotIndex))
        : new Set<number>();
      const currentAvailableSlotIndexes = [
        ...new Set([
          ...currentEmptySlotIndexes,
          ...importStore.formationReplacementSlotIndexes,
        ]),
      ]
        .filter((slotIndex) => currentVisibleSlotIndexes.has(slotIndex))
        .sort((a, b) => a - b);
      if (currentAvailableSlotIndexes.length === 0) {
        enqueueNoAvailableSlotAlert();
        return;
      }
      const selectedSlotIndexes = new Set(
        importStore.selectedDataList.map((item) => item.formationSlotIndex),
      );
      const formationSlotIndex = currentAvailableSlotIndexes.find(
        (slotIndex) => !selectedSlotIndexes.has(slotIndex),
      );
      if (formationSlotIndex == null) {
        enqueueNoAvailableSlotAlert();
        return;
      }

      importStore.addSelectedData({
        ...selected,
        formationSlotIndex,
        sourceWork: parentWork,
        sourceDetail: detail,
      });
      return;
    }

    if (standardModal.value == null) return;

    updateFormationSlotReference(
      standardModal.title,
      Number(standardModal.value),
      selected,
      parentWork,
      detail,
    );
    clearModal();
  };

  const importToEmptySlots = () => {
    if (useModalStore.getState().isClosing) return;
    if (!isBulkImport || !standardModal?.title) return;

    const currentSelections = useImportStationWorkStore.getState().selectedDataList;
    if (currentSelections.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }

    const currentPe = useProjectElementListStore.getState().projectElementList.find(
      (item) => item.id === standardModal.title,
    );
    if (currentPe?.projectElementType !== ProjectElementType.FORMATION) return;

    const currentEmptySlotIndexes = getEmptyFormationSlotIndexes(
      currentPe.formation.items,
      currentPe.formation.formationType,
    );
    const currentVisibleSlotIndexes = new Set(getVisibleFormationItems(
      currentPe.formation.items,
      currentPe.formation.formationType,
    ).map((item) => item.slotIndex));
    const currentReplacementSlotIndexes = useImportStationWorkStore
      .getState().formationReplacementSlotIndexes;
    const currentAvailableSlotIndexes = [
      ...new Set([...currentEmptySlotIndexes, ...currentReplacementSlotIndexes]),
    ]
      .filter((slotIndex) => currentVisibleSlotIndexes.has(slotIndex))
      .sort((a, b) => a - b);
    if (currentSelections.length > currentAvailableSlotIndexes.length) {
      enqueueNoAvailableSlotAlert();
      return;
    }

    const claimedSlotIndexes = new Set<number>();
    const assignments: FormationSlotReferenceAssignment[] = [];
    currentSelections.forEach((item) => {
      const assignedSlotIndex = item.formationSlotIndex;
      const slotIndex = assignedSlotIndex != null
        && currentAvailableSlotIndexes.includes(assignedSlotIndex)
        && !claimedSlotIndexes.has(assignedSlotIndex)
        ? assignedSlotIndex
        : currentAvailableSlotIndexes.find((index) => !claimedSlotIndexes.has(index));
      if (slotIndex == null) return;

      const currentItem = currentPe.formation.items.find(
        (formationItem) => formationItem.slotIndex === slotIndex,
      );
      claimedSlotIndexes.add(slotIndex);
      assignments.push({
        slotIndex,
        selected: item,
        work: item.sourceWork,
        detail: item.sourceDetail,
        replaceExisting: !!currentItem
          && getFormationItemSource(currentItem) !== 'empty'
          && currentReplacementSlotIndexes.includes(slotIndex),
      });
    });
    if (assignments.length !== currentSelections.length) {
      enqueueNoAvailableSlotAlert();
      return;
    }

    updateFormationSlotReferences(standardModal.title, assignments);
    clearModal();
  };

  const isSelected = (selected: SelectedData) => selectedDataList.some((item) => (
    item.workId === selected.workId && item.detailId === selected.detailId
  ));

  return {
    availableSlotIndexes,
    formationItems,
    formationType: targetFormation?.formationType,
    importToEmptySlots,
    importToSlot,
    isBulkImport,
    isSelected,
    selectedDataList,
  };
}
